import React, { useState, useEffect, useRef } from 'react';
import { Send, X, Edit2, DollarSign, CreditCard, AlertTriangle } from 'lucide-react';
import { teacherNegotiationService, teacherHiringService } from '../../firebase/tuition';
import { useRealtime } from '../../contexts/RealtimeContext';
import SmartPaymentModal from '../payment/SmartPaymentModal';
import toast from 'react-hot-toast';

const NegotiationChat = ({ negotiation, onClose, onPriceUpdate }) => {
  const { currentUser, userProfile } = useRealtime();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingPrice, setEditingPrice] = useState(false);
  const [newPrice, setNewPrice] = useState(negotiation?.proposedPrice || 0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const messagesEndRef = useRef(null);

  const isStudent = currentUser?.uid === negotiation?.studentId;
  const isTeacher = currentUser?.uid === negotiation?.teacherUserId;

  // Utility functions to detect and censor contact information
  const detectContactInfo = (text) => {
    if (!text) return { hasContactInfo: false, censoredText: text };
    
    let hasContactInfo = false;
    const replacements = [];

    // Detect email addresses
    const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    let emailMatch;
    while ((emailMatch = emailPattern.exec(text)) !== null) {
      hasContactInfo = true;
      replacements.push({
        start: emailMatch.index,
        end: emailMatch.index + emailMatch[0].length,
        type: 'email'
      });
    }

    // Detect phone numbers written as words (e.g., "one two three four five six seven eight nine zero")
    const numberWords = {
      'zero': '0', 'one': '1', 'two': '2', 'three': '3', 'four': '4',
      'five': '5', 'six': '6', 'seven': '7', 'eight': '8', 'nine': '9'
    };
    
    // Also handle concatenated number words (e.g., "onetwothreefour...")
    // Look for sequences of number words that might be concatenated
    const numberWordNames = Object.keys(numberWords).sort((a, b) => b.length - a.length); // Sort by length (longest first) for greedy matching
    
    // Function to extract number words from a string
    const extractNumberWords = (str) => {
      const lowerStr = str.toLowerCase();
      const words = [];
      let remaining = lowerStr;
      let position = 0;
      
      while (remaining.length > 0 && words.length < 10) {
        let matched = false;
        for (const word of numberWordNames) {
          if (remaining.startsWith(word)) {
            words.push({ word, position, length: word.length });
            remaining = remaining.substring(word.length);
            position += word.length;
            matched = true;
            break;
          }
        }
        if (!matched) break;
      }
      
      return words;
    };
    
    // Look for potential concatenated number word sequences
    // Check every position in the text for potential number word sequences
    for (let i = 0; i < text.length - 10; i++) {
      // Try to extract number words starting from this position
      // Check a reasonable window (up to 100 characters for 10 number words)
      const window = text.substring(i, Math.min(i + 100, text.length));
      const extractedWords = extractNumberWords(window);
      
      if (extractedWords.length === 10) {
        // Found 10 consecutive number words
        const digitSequence = extractedWords.map(w => numberWords[w.word]).join('');
        
        if (digitSequence.length === 10) {
          const start = i;
          const end = i + extractedWords[extractedWords.length - 1].position + extractedWords[extractedWords.length - 1].length;
          
          // Expand to include surrounding text/underscores
          let expandedStart = start;
          while (expandedStart > 0 && /[a-zA-Z_]/.test(text[expandedStart - 1])) {
            expandedStart--;
          }
          
          let expandedEnd = end;
          while (expandedEnd < text.length && /[a-zA-Z_]/.test(text[expandedEnd])) {
            expandedEnd++;
          }
          
          const overlaps = replacements.some(r => 
            (expandedStart >= r.start && expandedStart < r.end) || 
            (expandedEnd > r.start && expandedEnd <= r.end) ||
            (expandedStart <= r.start && expandedEnd >= r.end)
          );
          
          if (!overlaps) {
            hasContactInfo = true;
            replacements.push({ start: expandedStart, end: expandedEnd, type: 'phone' });
            // Skip ahead to avoid overlapping matches
            i = expandedEnd - 1;
          }
        }
      }
    }
    
    // Pattern to match number words with separators (case-insensitive)
    // Matches: "one two three...", "one-two-three...", "abone two three...", etc.
    const numberWordPattern = /(?:^|[^a-zA-Z])(zero|one|two|three|four|five|six|seven|eight|nine)(?:[^a-zA-Z]|$)/gi;
    
    // Find all sequences of number words that form exactly 10 digits
    let wordMatch;
    const wordMatches = [];
    const originalText = text; // Keep original for index tracking
    
    // Reset regex lastIndex
    numberWordPattern.lastIndex = 0;
    
    while ((wordMatch = numberWordPattern.exec(text)) !== null) {
      // Get the actual word (group 1) and its position
      const word = wordMatch[1].toLowerCase();
      const wordStart = wordMatch.index + (wordMatch[0].indexOf(wordMatch[1]));
      
      wordMatches.push({
        word: word,
        index: wordStart,
        length: wordMatch[1].length,
        fullMatchStart: wordMatch.index,
        fullMatchEnd: wordMatch.index + wordMatch[0].length
      });
    }
    
    // Look for sequences of 10 consecutive number words
    for (let i = 0; i <= wordMatches.length - 10; i++) {
      let sequence = [];
      let currentIndex = i;
      let startIndex = wordMatches[i].fullMatchStart;
      
      // Collect up to 10 consecutive number words
      while (sequence.length < 10 && currentIndex < wordMatches.length) {
        const currentMatch = wordMatches[currentIndex];
        const prevMatch = sequence.length > 0 ? wordMatches[currentIndex - 1] : null;
        
        // Check if this word is close enough to be part of the sequence
        if (prevMatch) {
          const gap = currentMatch.fullMatchStart - prevMatch.fullMatchEnd;
          // Allow gap of up to 10 characters (for spaces, dashes, surrounding text, etc.)
          if (gap > 10) break;
        }
        
        sequence.push(currentMatch);
        currentIndex++;
      }
      
      // If we found exactly 10 number words, convert to digits and check
      if (sequence.length === 10) {
        const digitSequence = sequence.map(m => numberWords[m.word]).join('');
        
        // Validate it's exactly 10 digits
        if (digitSequence.length === 10) {
          const endIndex = sequence[sequence.length - 1].fullMatchEnd;
          
          // Expand to include any immediately surrounding text/underscores (like digit detection)
          // Look for text/underscores before the first word
          let expandedStart = startIndex;
          while (expandedStart > 0 && /[a-zA-Z_]/.test(text[expandedStart - 1])) {
            expandedStart--;
          }
          
          // Look for text/underscores after the last word
          let expandedEnd = endIndex;
          while (expandedEnd < text.length && /[a-zA-Z_]/.test(text[expandedEnd])) {
            expandedEnd++;
          }
          
          // Check if this overlaps with any existing replacement
          const overlaps = replacements.some(r => 
            (expandedStart >= r.start && expandedStart < r.end) || 
            (expandedEnd > r.start && expandedEnd <= r.end) ||
            (expandedStart <= r.start && expandedEnd >= r.end)
          );
          
          if (!overlaps) {
            hasContactInfo = true;
            // Hide the entire sequence including any surrounding text/underscores
            replacements.push({ start: expandedStart, end: expandedEnd, type: 'phone' });
          }
        }
      }
    }

    // Detect 10-digit phone numbers (including all variations and derivatives)
    // This catches: 1234567890, 1234567890_, ab1234567890, 12345 67890, +91 1234567890, etc.
    
    // Find all potential 10-digit sequences (with optional spaces/dashes, surrounding text, etc.)
    const phonePatterns = [
      // Pattern 1: +91 followed by 10 digits (with optional spaces/dashes)
      /\+91[\s-]?\d{10}/g,
      // Pattern 2: 10 digits with spaces/dashes in the middle: 12345 67890 or 12345-67890
      /\d{5}[\s-]?\d{5}/g,
      // Pattern 3: 10 consecutive digits that might have text/underscores immediately before or after
      // Matches: ab1234567890, 1234567890_, abc1234567890def, 1234567890abc, etc.
      /[a-zA-Z_]*\d{10}[a-zA-Z_]*/g,
      // Pattern 4: 10 digits with optional spaces after (catches "1234567890 please" but not "call me 1234567890")
      /\d{10}\s+/g,
      // Pattern 5: Pure 10-digit sequences with word boundaries (catches standalone numbers)
      /\b\d{10}\b/g
    ];

    phonePatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        const matchedText = match[0];
        // Extract all digits from the match
        const digitsOnly = matchedText.replace(/\D/g, '');
        
        // Validate: must contain exactly 10 digits
        if (digitsOnly.length === 10) {
          const start = match.index;
          const end = start + matchedText.length;
          
          // Check if this overlaps with any existing replacement
          const overlaps = replacements.some(r => 
            (start >= r.start && start < r.end) || 
            (end > r.start && end <= r.end) ||
            (start <= r.start && end >= r.end)
          );
          
          if (!overlaps) {
            hasContactInfo = true;
            // Hide the entire matched pattern (including any surrounding text/underscores)
            // This handles cases like "ab1234567890" or "1234567890_" by hiding the whole thing
            replacements.push({ start, end, type: 'phone' });
          }
        }
      }
    });

    // Sort replacements by start index (descending) to replace from end to start
    replacements.sort((a, b) => b.start - a.start);

    // Apply replacements from end to start to maintain indices
    let censoredText = text;
    replacements.forEach(replacement => {
      censoredText = censoredText.substring(0, replacement.start) + 
                    '***HIDDEN***' + 
                    censoredText.substring(replacement.end);
    });

    return { hasContactInfo, censoredText };
  };

  const censorMessage = (messageText) => {
    if (!messageText) return messageText;
    const { censoredText } = detectContactInfo(messageText);
    return censoredText;
  };

  useEffect(() => {
    if (!negotiation?.id) return;

    // Subscribe to messages
    const unsubscribeMessages = teacherNegotiationService.subscribeToNegotiationMessages(
      negotiation.id,
      (result) => {
        setLoading(false);
        if (result.success) {
          setMessages(result.data || []);
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else {
          console.error('Error loading messages:', result.error);
        }
      }
    );

    // Subscribe to negotiation updates
    const unsubscribeNegotiation = teacherNegotiationService.subscribeToNegotiation(
      negotiation.id,
      (result) => {
        if (result.success && result.data) {
          // Update negotiation status - only close if rejected or closed, not accepted
          if (result.data.status === 'rejected' || result.data.status === 'closed') {
            toast.success(`Negotiation ${result.data.status}`);
            if (onClose) {
              setTimeout(() => onClose(), 2000);
            }
          } else if (result.data.status === 'accepted') {
            toast.success('Negotiation accepted! You can now proceed to hire the teacher.');
            // Don't close the chat - let student hire at negotiated price
          }
        }
      }
    );

    return () => {
      if (unsubscribeMessages) unsubscribeMessages();
      if (unsubscribeNegotiation) unsubscribeNegotiation();
    };
  }, [negotiation?.id, onClose]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser || sending) return;

    // Check for contact information before sending
    const { hasContactInfo } = detectContactInfo(newMessage);
    if (hasContactInfo) {
      toast.error('Contact information (phone numbers or email addresses) is not allowed. Please remove it before sending.');
      return;
    }

    try {
      setSending(true);
      
      // If teacher is sending first message and negotiation is pending, activate it
      if (isTeacher && negotiation.status === 'pending') {
        await teacherNegotiationService.activateNegotiation(negotiation.id);
      }
      
      await teacherNegotiationService.sendNegotiationMessage(negotiation.id, {
        senderId: currentUser.uid,
        senderName: userProfile?.displayName || currentUser.displayName || 'Anonymous',
        senderEmail: currentUser.email,
        message: newMessage.trim(),
        messageType: 'text'
      });

      setNewMessage('');
    } catch (error) {
      toast.error('Error sending message: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleUpdatePrice = async () => {
    if (!isStudent) return;
    if (newPrice <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }

    try {
      await teacherNegotiationService.updateNegotiation(negotiation.id, {
        proposedPrice: parseFloat(newPrice),
        priceUpdatedAt: new Date().toISOString()
      });

      // Send a message about price update
      await teacherNegotiationService.sendNegotiationMessage(negotiation.id, {
        senderId: currentUser.uid,
        senderName: userProfile?.displayName || currentUser.displayName || 'Anonymous',
        senderEmail: currentUser.email,
        message: `Updated proposed price to ₹${newPrice}`,
        messageType: 'system'
      });

      setEditingPrice(false);
      toast.success('Price updated successfully');
      if (onPriceUpdate) {
        onPriceUpdate(parseFloat(newPrice));
      }
    } catch (error) {
      toast.error('Error updating price: ' + error.message);
    }
  };

  const handleAccept = async () => {
    if (!isTeacher) return;
    try {
      // If negotiation is still pending, activate it first, then accept
      if (negotiation.status === 'pending') {
        await teacherNegotiationService.activateNegotiation(negotiation.id);
      }
      await teacherNegotiationService.acceptNegotiation(negotiation.id);
      toast.success('Negotiation accepted! Student can now proceed to hire at the negotiated price.');
    } catch (error) {
      toast.error('Error accepting negotiation: ' + error.message);
    }
  };

  const handleReject = async () => {
    if (!isTeacher) return;
    try {
      await teacherNegotiationService.rejectNegotiation(negotiation.id);
      toast.success('Negotiation rejected');
    } catch (error) {
      toast.error('Error rejecting negotiation: ' + error.message);
    }
  };

  const handleClose = async () => {
    try {
      await teacherNegotiationService.closeNegotiation(negotiation.id);
      if (onClose) onClose();
    } catch (error) {
      toast.error('Error closing negotiation: ' + error.message);
    }
  };

  const handleHireAtNegotiatedPrice = () => {
    if (!currentUser || !negotiation) return;

    const hiringData = negotiation.hiringData || {};
    const negotiatedPrice = negotiation.proposedPrice || negotiation.originalPrice;
    const subjectsArray = Array.isArray(hiringData.subjects) 
      ? hiringData.subjects 
      : (hiringData.subject ? [hiringData.subject] : []);
    const subjectsText = subjectsArray.length > 0 
      ? subjectsArray.join(', ') 
      : 'Multiple subjects';

    const paymentInfo = {
      type: 'teacher_hiring',
      amount: negotiatedPrice,
      currency: 'INR',
      description: `Hire ${negotiation.teacherName} for ${subjectsText} at negotiated price`,
      merchantName: 'Mahstry',
      customerName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Student',
      customerEmail: currentUser.email,
      customerPhone: currentUser.phoneNumber || '',
      userId: currentUser.uid,
      hiringData: {
        ...hiringData,
        subjects: subjectsArray,
        subject: subjectsArray.length === 1 ? subjectsArray[0] : subjectsText, // For backward compatibility
        teacherId: negotiation.teacherId,
        teacherName: negotiation.teacherName,
        studentId: currentUser.uid,
        studentName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Student',
        studentEmail: currentUser.email,
        feesPerHour: hiringData.feesPerHour || 0,
        sessionCost: negotiatedPrice,
        totalAmount: negotiatedPrice,
        negotiatedPrice: negotiatedPrice,
        originalPrice: negotiation.originalPrice,
        negotiationId: negotiation.id
      }
    };

    setPaymentData(paymentInfo);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async (response, transactionData) => {
    try {
      // Complete the teacher hiring process with negotiated price
      const result = await teacherHiringService.hireTeacher({
        ...transactionData.hiringData,
        paymentId: transactionData.paymentId,
        paymentStatus: 'completed',
        negotiatedPrice: transactionData.hiringData.negotiatedPrice,
        originalPrice: transactionData.hiringData.originalPrice
      });

      // Close the negotiation
      await teacherNegotiationService.closeNegotiation(negotiation.id);
      
      toast.success('Teacher hired successfully at negotiated price! The teacher will create a batch when ready.');
      setShowPaymentModal(false);
      if (onClose) {
        setTimeout(() => onClose(), 1000);
      }
    } catch (error) {
      toast.error('Payment successful but failed to complete hiring. Please contact support.');
      console.error('Error completing hiring:', error);
    }
  };

  const handlePaymentFailure = (error) => {
    toast.error('Payment failed. Please try again.');
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp.seconds * 1000);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const isOwnMessage = (message) => {
    return message.senderId === currentUser?.uid;
  };

  if (!negotiation) {
    return null;
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-lg">
              {isStudent ? negotiation.teacherName : negotiation.studentName}
            </h3>
            <p className="text-sm opacity-90">
              {negotiation.status === 'pending' && 'Waiting for teacher...'}
              {negotiation.status === 'active' && 'Negotiation in progress'}
              {negotiation.status === 'accepted' && 'Negotiation accepted'}
              {negotiation.status === 'rejected' && 'Negotiation rejected'}
              {negotiation.status === 'closed' && 'Negotiation closed'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors p-1"
            title="Close negotiation"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="px-4 py-2.5 bg-red-50 border-b-2 border-red-300 flex-shrink-0">
        <div className="flex items-start gap-2">
          <AlertTriangle className="text-red-600 mt-0.5 flex-shrink-0" size={18} />
          <p className="text-xs text-red-800 font-semibold">
            ⚠️ WARNING: Do not share any contact information (phone numbers, email addresses, etc.) in this chat. 
            Violators will be liable to penalties and strict actions.
          </p>
        </div>
      </div>

      {/* Price Info */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Original Price</p>
            <p className="text-lg font-semibold text-gray-900">₹{negotiation.originalPrice || 0}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Proposed Price</p>
            {editingPrice && isStudent ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                  min="1"
                />
                <button
                  onClick={handleUpdatePrice}
                  className="px-2 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingPrice(false);
                    setNewPrice(negotiation.proposedPrice || 0);
                  }}
                  className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold text-blue-600">₹{negotiation.proposedPrice || 0}</p>
                {isStudent && negotiation.status !== 'accepted' && negotiation.status !== 'rejected' && negotiation.status !== 'closed' && (
                  <button
                    onClick={() => setEditingPrice(true)}
                    className="p-1 text-gray-500 hover:text-blue-600"
                    title="Edit price"
                  >
                    <Edit2 size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Teacher Actions - Show at any time for pending or active negotiations */}
      {isTeacher && (negotiation.status === 'pending' || negotiation.status === 'active') && (
        <div className="px-4 py-2 bg-blue-50 border-b border-gray-200 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-xs text-gray-600">Proposed: <span className="font-semibold text-blue-600">₹{negotiation.proposedPrice || 0}</span></p>
              <p className="text-xs text-gray-600">Original: <span className="font-semibold text-gray-700">₹{negotiation.originalPrice || 0}</span></p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAccept}
              className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
            >
              Accept
            </button>
            <button
              onClick={handleReject}
              className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
            >
              Reject
            </button>
          </div>
        </div>
      )}

      {/* Student Action - Hire at Negotiated Price when accepted */}
      {isStudent && negotiation.status === 'accepted' && (
        <div className="px-4 py-3 bg-green-50 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm font-semibold text-green-800">Negotiation Accepted!</p>
              <p className="text-xs text-gray-600">Final Price: <span className="font-bold text-green-700">₹{negotiation.proposedPrice || 0}</span></p>
            </div>
          </div>
          <button
            onClick={handleHireAtNegotiatedPrice}
            className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
          >
            <CreditCard size={18} />
            Hire Teacher at ₹{negotiation.proposedPrice || 0}
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading messages...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-gray-600">No messages yet. Start the conversation!</p>
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${isOwnMessage(message) ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs px-3 py-2 rounded-lg ${
                  message.messageType === 'system'
                    ? 'bg-yellow-100 text-yellow-800 mx-auto text-center'
                    : isOwnMessage(message)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                {message.messageType !== 'system' && (
                  <div className={`text-xs font-semibold mb-1 ${
                    isOwnMessage(message) ? 'text-blue-100' : 'text-gray-600'
                  }`}>
                    {message.senderName || 'Anonymous'}
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap break-words">{censorMessage(message.message)}</p>
                <p className={`text-xs mt-1 ${
                  message.messageType === 'system'
                    ? 'text-yellow-700'
                    : isOwnMessage(message)
                    ? 'text-blue-100'
                    : 'text-gray-500'
                }`}>
                  {formatTime(message.createdAt)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input - Allow students to message even if teacher accepted without chatting */}
      {(negotiation.status === 'active' || 
        (isStudent && (negotiation.status === 'pending' || negotiation.status === 'accepted')) || 
        (isTeacher && (negotiation.status === 'pending' || negotiation.status === 'active'))) && (
        <div className="px-4 py-3 border-t border-gray-200 flex-shrink-0">
          <form onSubmit={handleSendMessage} className="flex items-center gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => {
                // Prevent pasting files
                const input = e.target;
                if (input.files && input.files.length > 0) {
                  toast.error('File uploads are not allowed in negotiation chat.');
                  return;
                }
                setNewMessage(e.target.value);
              }}
              onPaste={(e) => {
                // Check if pasted content contains files
                const items = e.clipboardData?.items;
                if (items) {
                  for (let i = 0; i < items.length; i++) {
                    const item = items[i];
                    if (item.kind === 'file') {
                      e.preventDefault();
                      toast.error('File uploads (PDF/images) are not allowed in negotiation chat.');
                      return;
                    }
                  }
                }
              }}
              placeholder="Type a message... (No contact info or files allowed)"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <Send size={20} />
              )}
            </button>
          </form>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && paymentData && (
        <SmartPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          paymentData={paymentData}
          onSuccess={handlePaymentSuccess}
          onFailure={handlePaymentFailure}
          title="Complete Hiring at Negotiated Price"
        />
      )}
    </div>
  );
};

export default NegotiationChat;

